import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Flag,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TransactionItem } from "../../types";
import { TransactionStatusBadge } from "../TransactionStatusBadge";

interface TransactionDetailsHeaderProps {
  transaction: TransactionItem;
  status: string;
  isFlagged?: boolean;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onNavigateSibling?: (delta: number) => void;
  onReconcile: () => void;
  onDownloadReceipt: () => void;
  onFlagTransaction: () => void;
}

export const TransactionDetailsHeader: React.FC<TransactionDetailsHeaderProps> = ({
  transaction,
  status,
  isFlagged,
  canGoPrevious = false,
  canGoNext = false,
  onNavigateSibling,
  onReconcile,
  onDownloadReceipt,
  onFlagTransaction,
}) => {
  const navigate = useNavigate();

  const currency = (transaction.currency || "USD").toUpperCase();
  const absAmount = Math.abs(Number(transaction.amount) || 0);
  const formattedNum = absAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let symbol = "";
  if (currency === "USD") symbol = "$";
  else if (currency === "GBP") symbol = "£";
  else if (currency === "EUR") symbol = "€";
  else if (currency === "NGN") symbol = "₦";

  // Subtitle / category name
  const subtitle =
    transaction.category ||
    (transaction.TransactionType === "OTC"
      ? "OTC Request"
      : currency === "USD"
        ? "Same currency transfer"
        : "Invoice payment");

  const displayStatus = isFlagged ? "Flagged" : status;

  return (
    <div className="flex flex-col gap-4">
      {/* Top navigation row: Back button on left, Sibling navigation on right */}
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex h-10 items-center gap-2 text-[13px] font-medium text-[#6F00FF] transition-colors hover:text-[#5800CC]"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>

        {/* Sibling navigation chevrons */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onNavigateSibling?.(-1)}
            disabled={!canGoPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Previous transaction"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => onNavigateSibling?.(1)}
            disabled={!canGoNext}
            className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Next transaction"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Main Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] font-normal text-[#667085]">
            {subtitle}
          </span>
          <div className="flex items-center gap-3">
            <h1 className="text-[28px] font-bold tracking-tight text-[#101828]">
              {symbol}
              {formattedNum}
              <span className="ml-2 text-[20px] font-normal text-[#667085]">
                {currency}
              </span>
            </h1>
            <TransactionStatusBadge status={displayStatus} />
          </div>
        </div>

        {/* More Actions Dropdown */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="More actions"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E4E7EC] bg-white text-[#344054] shadow-sm transition-colors hover:bg-[#F9FAFB] hover:text-[#101828]"
            >
              <MoreHorizontal size={20} />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 min-w-[200px] overflow-hidden rounded-xl border border-[#EEF1FB] bg-white p-1.5 shadow-[0_12px_28px_rgba(16,24,40,0.14)] animate-in fade-in-0 zoom-in-95"
            >
              <DropdownMenu.Item
                onClick={onReconcile}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-[#344054] outline-none transition-colors hover:bg-[#F8F9FF] hover:text-[#101828]"
              >
                <RefreshCw size={16} className="text-[#667085]" />
                <span>Reconcile</span>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onClick={onDownloadReceipt}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-[#344054] outline-none transition-colors hover:bg-[#F8F9FF] hover:text-[#101828]"
              >
                <Download size={16} className="text-[#667085]" />
                <span>Download receipt</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-[#F2F4F7]" />

              <DropdownMenu.Item
                disabled={isFlagged}
                onClick={isFlagged ? undefined : onFlagTransaction}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium outline-none transition-colors ${
                  isFlagged
                    ? "cursor-not-allowed opacity-50 text-[#98A2B3]"
                    : "cursor-pointer text-[#B54708] hover:bg-[#FFFAEB]"
                }`}
              >
                <Flag size={16} className={isFlagged ? "text-[#98A2B3]" : "text-[#B54708]"} />
                <span>Flag transaction</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
};
